import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _defineProperty from "/usr/app/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import React from "react";
var __jsx = React.createElement;
import { useEffect, useState, useMemo } from "react";
import { useLang } from "@/common/Contexts/LanguageContext";
import { trackEvents } from "@/common/events/events";
import { getPlatformInfo } from "@/utils/GlobalJavascriptFunction";
import { URL_SELL_TRADE_FORM } from "@/pages/module-sell-trade/SellTradeContext";
import useHelpers from "@hooks/useHelpers";
import useIsMobile from "@hooks/useIsMobile";
import useBrandsData from "@hooks/useBrandsData";
import { ucWords } from "@/utils/stringHelpers";

// Import components
import HeroSection from "./components/HeroSection";
import QuickFormSection from "./components/QuickFormSection";
import ProcessStepsSection from "./components/ProcessStepsSection";
import PromiseBannerSection from "./components/PromiseBannerSection";
import StatisticsSection from "./components/StatisticsSection";
import BenefitsComparison from "./components/BenefitsComparison";
import FAQSection from "./components/FAQSection";
import FinalCTASection from "./components/FinalCTASection";

/**
 * Enhanced Sell Trade Landing Page Component with Tailwind CSS
 * 
 * This component implements the A/B test variant of the sell-trade landing page
 * with improved UI/UX based on Figma designs to decrease dropoff rates.
 */
var SellTradeLanding = function SellTradeLanding() {
  var lang = useLang();
  var isArabic = lang === 'ar';
  var isMobile = useIsMobile();
  var _useHelpers = useHelpers(),
    navigateTo = _useHelpers.navigateTo;
  // Initialize with pre-filled data from URL params or default
  var _useState = useState(function () {
      if (typeof window !== 'undefined') {
        var urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('make') || '';
      }
      return '';
    }),
    selectedMake = _useState[0],
    setSelectedMake = _useState[1];
  var _useState2 = useState(function () {
      if (typeof window !== 'undefined') {
        var urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('model') || '';
      }
      return '';
    }),
    selectedModel = _useState2[0],
    setSelectedModel = _useState2[1];
  var _useState3 = useState(function () {
      if (typeof window !== 'undefined') {
        var urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('trim') || '';
      }
      return '';
    }),
    selectedTrim = _useState3[0],
    setSelectedTrim = _useState3[1];
  var _useState4 = useState(function () {
      if (typeof window !== 'undefined') {
        var urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('year') || '';
      }
      return '';
    }),
    selectedYear = _useState4[0],
    setSelectedYear = _useState4[1];

  // Get real car data from lookup files
  var _useBrandsData = useBrandsData(),
    makes = _useBrandsData.makes,
    models = _useBrandsData.models,
    extensions = _useBrandsData.extensions,
    years = _useBrandsData.years,
    getModelsByMakeId = _useBrandsData.getModelsByMakeId,
    getExtensionsByModelId = _useBrandsData.getExtensionsByModelId,
    getMakeById = _useBrandsData.getMakeById,
    getModelById = _useBrandsData.getModelById,
    isLoading = _useBrandsData.isLoading;

  // Transform data for components
  var carMakes = useMemo(function () {
    return makes.map(function (make) {
      return {
        id: make.id,
        name: ucWords(make.name),
        logo: make.logo,
        seo_url: make.seo_url
      };
    });
  }, [makes]);
  var carModels = useMemo(function () {
    if (!selectedMake) return {};
    var makeId = parseInt(selectedMake);
    var modelsForMake = getModelsByMakeId(makeId);
    return _defineProperty({}, selectedMake, modelsForMake.map(function (model) {
      return {
        id: model.id,
        name: ucWords(model.name),
        seo_name: model.seo_name
      };
    }));
  }, [selectedMake, getModelsByMakeId]);
  var carTrims = useMemo(function () {
    if (!selectedModel || !selectedMake) return [];
    var modelId = parseInt(selectedModel);
    var makeId = parseInt(selectedMake);
    return getExtensionsByModelId(modelId, makeId).map(function (trim) {
      return {
        id: trim.id,
        name: ucWords(trim.name)
      };
    });
  }, [selectedModel, selectedMake, getExtensionsByModelId]);

  // Get available years based on selected model
  var getYearsForModel = function getYearsForModel(modelId) {
    if (!modelId) return [];
    var modelData = getModelById(parseInt(modelId));
    var currentYear = new Date().getFullYear();

    // For newer models (introduced after 2020), limit years from 2018
    // For older models, use full range from 2016
    var startYear = modelData && modelData.id > 1000 ? 2018 : 2016;
    return Array.from({
      length: currentYear + 2 - startYear
    }, function (v, i) {
      return (currentYear + 1 - i).toString();
    });
  };
  var availableYears = useMemo(function () {
    return selectedModel ? getYearsForModel(selectedModel) : [];
  }, [selectedModel, getModelById]);
  useEffect(function () {
    // Track the landing page view for analytics
    trackEvents("enhanced_sell_trade_landing_view", {
      platform: getPlatformInfo()
    });

    // Reset model and trim when make changes
    setSelectedModel("");
    setSelectedTrim("");
    setSelectedYear(""); // Also reset year when make changes
  }, [selectedMake]);
  useEffect(function () {
    // Reset year when model changes
    setSelectedYear("");
  }, [selectedModel]);
  var handleStartProcess = function handleStartProcess() {
    // Record user's car selection if any
    var selectedMakeData = getMakeById(parseInt(selectedMake));
    var selectedModelData = getModelById(parseInt(selectedModel));
    var carData = {
      make_id: selectedMake,
      make_name: (selectedMakeData === null || selectedMakeData === void 0 ? void 0 : selectedMakeData.name) || '',
      model_id: selectedModel,
      model_name: (selectedModelData === null || selectedModelData === void 0 ? void 0 : selectedModelData.name) || '',
      trim_id: selectedTrim,
      year: selectedYear
    };

    // Track the CTA click event
    trackEvents("sell_car_form_landing", {
      platform: getPlatformInfo()
    });
    var params = _objectSpread(_objectSpread({}, carData), {}, {
      'from': 'sell_trade_v2'
    });
    sessionStorage.setItem('sell_trade_v2', JSON.stringify(true));

    // Navigate to the form page
    navigateTo(URL_SELL_TRADE_FORM, params);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "text-[#333] w-full overflow-x-hidden"
  }, /*#__PURE__*/React.createElement(HeroSection, {
    onStartProcess: handleStartProcess
  }), /*#__PURE__*/React.createElement(QuickFormSection, {
    selectedMake: selectedMake,
    onMakeChange: function onMakeChange(makeId) {
      setSelectedMake(makeId);
      setSelectedModel("");
      setSelectedTrim("");
    },
    onStartProcess: handleStartProcess,
    carMakes: carMakes
  }), /*#__PURE__*/React.createElement(ProcessStepsSection, null), /*#__PURE__*/React.createElement(PromiseBannerSection, null), /*#__PURE__*/React.createElement(StatisticsSection, null), /*#__PURE__*/React.createElement(BenefitsComparison, null), /*#__PURE__*/React.createElement(FAQSection, null), /*#__PURE__*/React.createElement(FinalCTASection, {
    selectedMake: selectedMake,
    selectedModel: selectedModel,
    selectedTrim: selectedTrim,
    selectedYear: selectedYear,
    onMakeChange: function onMakeChange(makeId) {
      setSelectedMake(makeId);
      setSelectedModel("");
      setSelectedTrim("");
    },
    onModelChange: function onModelChange(modelId) {
      setSelectedModel(modelId);
      setSelectedTrim("");
      setSelectedYear(""); // Reset year when model changes
    },
    onTrimChange: setSelectedTrim,
    onYearChange: setSelectedYear,
    onStartProcess: handleStartProcess,
    carMakes: carMakes,
    carModels: carModels,
    carTrims: carTrims,
    carYears: availableYears
  }));
};
export default SellTradeLanding;